import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';
import { useAthena, Message as AthenaMessage } from './useAthena';
import { SelectedElement } from './useElementSelector';
import { useUserProfile } from './useUserProfile';
export interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
}
export interface Chat {
  id: string;
  title: string;
  messages: Message[];
  pinned: boolean;
  updatedAt: Date;
}
export interface ViewContext {
  name: string;
  description: string;
}
interface ChatContextType {
  chats: Chat[];
  activeChatId: string | null;
  isStreaming: boolean;
  setActiveChatId: (id: string | null) => void;
  sendMessage: (content: string, viewContext?: ViewContext, selectedElement?: SelectedElement | null, onClearSelection?: () => void) => Promise<void>;
  handleNewChat: () => void;
  togglePin: (id: string) => void;
  deleteChat: (id: string) => void;
  createChat: (initialMessage: string) => string;
}
const ChatContext = createContext<ChatContextType | null>(null);
const MOCK_CHATS: Chat[] = [{
  id: 'seed-1',
  title: 'EPT-2 vs EC IFS Run Delta',
  pinned: true,
  updatedAt: new Date(Date.now() - 1000 * 60 * 30),
  messages: [{
    id: 'seed-1-m1',
    role: 'user',
    content: 'Show me the run delta between EPT-2 12z and EC IFS 12z for wind 100m across Central Europe for the next 5 days.',
    timestamp: new Date(Date.now() - 1000 * 60 * 30)
  }, {
    id: 'seed-1-m2',
    role: 'assistant',
    content: `**Run Delta: EPT-2 12z vs EC IFS 12z — Wind 100m, Central Europe**
Confidence: MED · Generated: 12z cycle

| Timeframe | EPT-2 (m/s) | EC IFS (m/s) | Δ Delta | % Spread | Signal |
|-----------|-------------|--------------|---------|----------|--------|
| D+0 | 8.4 | 7.9 | +0.5 | +6.3% | → Stable |
| D+1 | 11.2 | 9.4 | +1.8 | +19.1% | ⚠ DIVERGE |
| D+2 | 9.7 | 8.1 | +1.6 | +19.8% | ⚠ DIVERGE |
| D+3 | 7.3 | 7.6 | -0.3 | -3.9% | → Stable |
| D+4 | 6.1 | 5.8 | +0.3 | +5.2% | → Stable |

⚠ **TRADING SIGNAL — D+1 to D+2**: EPT-2 is running ~19% higher than EC IFS. This spread exceeds the 15% threshold.

**Market Implication**: EPT-2's bullish wind signal for D+1–D+2 suggests potential generation surplus in DE/AT. If EPT-2 verifies, expect downward pressure on DA prices for those delivery periods. Consider short DA positions or ID arbitrage if IFS converges upward in the 06z cycle.

**Recommended action**: Monitor 06z EC IFS update. If IFS closes gap to EPT-2, the bullish wind signal strengthens — increase short DA exposure.`,
    timestamp: new Date(Date.now() - 1000 * 60 * 29)
  }, {
    id: 'seed-1-m3',
    role: 'user',
    content: 'What about the 06z vs 12z delta within EPT-2 itself?',
    timestamp: new Date(Date.now() - 1000 * 60 * 20)
  }, {
    id: 'seed-1-m4',
    role: 'assistant',
    content: `**EPT-2 Internal Run Delta: 06z vs 12z — Wind 100m, Central Europe**

| Timeframe | EPT-2 06z | EPT-2 12z | Δ | Direction | Confidence |
|-----------|-----------|-----------|---|-----------|------------|
| D+0 | 8.1 | 8.4 | +0.3 | ↑ Slight upgrade | HIGH |
| D+1 | 10.3 | 11.2 | +0.9 | ↑ Bullish revision | HIGH |
| D+2 | 8.8 | 9.7 | +0.9 | ↑ Bullish revision | MED |
| D+3 | 7.5 | 7.3 | -0.2 | ↓ Minor downgrade | MED |
| D+4 | 6.4 | 6.1 | -0.3 | ↓ Minor downgrade | LOW |

**Key takeaway**: EPT-2 has consistently upgraded D+1–D+2 wind across both cycles. This is a persistent bullish signal, not noise. The 12z run reinforces the D+1–D+2 generation surplus thesis.

**Trading implication**: Two consecutive upgrades in the same timeframe = HIGH conviction. D+1 short DA position in DE has strong model backing. ↑ Confidence: HIGH`,
    timestamp: new Date(Date.now() - 1000 * 60 * 19)
  }]
}, {
  id: 'seed-2',
  title: 'DA/ID Price Spread — DE, FR, NL',
  pinned: true,
  updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 3),
  messages: [{
    id: 'seed-2-m1',
    role: 'user',
    content: 'Analyze day-ahead vs intraday price spreads for DE, FR, and NL. Any arbitrage opportunities?',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 3)
  }, {
    id: 'seed-2-m2',
    role: 'assistant',
    content: `**DA vs ID Price Spread Analysis — DE · FR · NL**
Data: ENTSOE · Cycle: 12z update

| Market | DA Price (€/MWh) | ID Price (€/MWh) | Spread | Direction | Primary Driver |
|--------|-----------------|-----------------|--------|-----------|----------------|
| DE | 87.4 | 79.2 | -8.2 | ↓ ID cheaper | Wind upgrade D+1 |
| FR | 91.1 | 94.3 | +3.2 | ↑ ID premium | Nuclear outage update |
| NL | 88.6 | 81.4 | -7.2 | ↓ ID cheaper | Wind + DE coupling |

**Top Arbitrage Opportunities**:

1. **DE ID Buy / DA Sell** — Spread: -8.2 €/MWh ↑ HIGH confidence
   - EPT-2 wind upgrade for D+1 not yet priced into DA
   - Buy ID, sell DA → capture ~8 €/MWh if wind verifies

2. **FR DA Buy / ID Sell** — Spread: +3.2 €/MWh MED confidence
   - Nuclear outage uncertainty inflating ID
   - If outage resolves, ID softens → short ID, long DA

3. **DE-NL Coupling Play** — Spread convergence expected
   - Both markets showing similar wind-driven ID discount
   - Cross-border flow from NL→DE likely to compress spreads

**Trading implication**: DE is the highest-conviction opportunity. EPT-2's D+1 wind upgrade is the catalyst — if 18z EC IFS confirms, execute DE ID buy immediately.`,
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 3 + 8000)
  }, {
    id: 'seed-2-m3',
    role: 'user',
    content: 'What interconnector flows should I watch to validate the DE opportunity?',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2)
  }, {
    id: 'seed-2-m4',
    role: 'assistant',
    content: `**Interconnector Flows — DE Validation Checklist**

| Border | Current Flow | Direction | Capacity | Utilization | Price Δ | Watch For |
|--------|-------------|-----------|----------|-------------|---------|-----------|
| DE→FR | 1,840 MW | → Export | 3,100 MW | 59% | DE -3.7 | Reversal if FR ID rises |
| NL→DE | 2,100 MW | → Import | 2,500 MW | 84% | NL -7.2 | Near capacity — watch curtailment |
| AT→DE | 890 MW | → Import | 1,500 MW | 59% | AT -2.1 | Stable, low risk |
| DK→DE | 1,650 MW | → Import | 2,000 MW | 83% | DK wind | Key wind import corridor |

**Key signals to monitor**:
- **NL→DE at 84% utilization** ⚠ — if wind ramps further, this corridor saturates. Excess NL wind gets stranded, widening NL discount further.
- **DK→DE corridor** — Denmark wind forecast aligns with EPT-2 upgrade. If DK wind ramps, additional cheap imports reinforce DE ID discount.
- **DE→FR reversal risk** — if FR nuclear outage extends, FR prices spike and DE→FR flow increases, drawing down DE surplus. Monitor RTE outage bulletins.

**Validation trigger**: DK→DE flow increase + NL→DE near saturation = wind surplus confirmed → execute DE ID buy.`,
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2 + 6000)
  }]
}, {
  id: 'seed-3',
  title: 'North Sea Wind Outlook D+1–D+5',
  pinned: false,
  updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 5),
  messages: [{
    id: 'seed-3-m1',
    role: 'user',
    content: 'Multi-model wind generation outlook for North Sea and Germany D+1 through D+5.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 5)
  }, {
    id: 'seed-3-m2',
    role: 'assistant',
    content: `**Multi-Model Wind Generation Outlook — North Sea + Germany**
D+1 through D+5 · Capacity Factor (%) · 12z cycle

| Timeframe | EPT-2 | EC AIFS | EC IFS | GFS | Max Spread | Signal |
|-----------|-------|---------|--------|-----|------------|--------|
| D+1 | 68% | 65% | 57% | 61% | 11pp | ⚠ Diverge |
| D+2 | 71% | 69% | 60% | 58% | 13pp | ⚠ Diverge |
| D+3 | 58% | 56% | 55% | 54% | 4pp | → Stable |
| D+4 | 42% | 44% | 46% | 48% | 6pp | → Stable |
| D+5 | 31% | 35% | 38% | 41% | 10pp | MED spread |

**Generation in GW (Germany offshore + onshore combined)**:

| Timeframe | EPT-2 | EC IFS | Δ GW | Ramp Risk |
|-----------|-------|--------|------|-----------|
| D+1 | 38.2 | 32.1 | +6.1 | ↑ Ramp up |
| D+2 | 39.8 | 33.7 | +6.1 | → Sustained |
| D+3 | 32.5 | 30.9 | +1.6 | ↓ Ramp down |
| D+4 | 23.6 | 25.8 | -2.2 | ↓ Declining |
| D+5 | 17.4 | 21.3 | -3.9 | ↓ Bearish |

**Summary**: AI models (EPT-2, EC AIFS) are significantly more bullish than NWP models (EC IFS, GFS) for D+1–D+2. This is a classic AI vs NWP divergence pattern. Historical verification favors EPT-2 in high-wind regimes.

**Trading implication**: D+1–D+2 bullish wind → short DA. D+4–D+5 bearish convergence → consider long DA for those delivery periods. The D+3 transition is the key inflection point.`,
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 5 + 7000)
  }]
}, {
  id: 'seed-4',
  title: 'Solar Ramp Risk — Italy & S. Germany',
  pinned: false,
  updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 8),
  messages: [{
    id: 'seed-4-m1',
    role: 'user',
    content: 'Assess solar ramp risk for tomorrow across Southern Germany and Italy. Compare cloud cover forecasts and estimate generation impact.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 8)
  }, {
    id: 'seed-4-m2',
    role: 'assistant',
    content: `**Solar Ramp Risk Assessment — S. Germany + Italy**
D+1 · 12z cycle · Confidence: MED

**Cloud Cover Forecast (% coverage, daytime hours 08–18 UTC)**:

| Region | EPT-2 | EC IFS | GFS | Consensus | Risk Level |
|--------|-------|--------|-----|-----------|------------|
| S. Germany | 35% | 42% | 38% | 38% | MED ⚠ |
| N. Italy (Po Valley) | 18% | 22% | 25% | 22% | LOW ✓ |
| C. Italy | 28% | 31% | 29% | 29% | MED ⚠ |
| Sicily | 8% | 12% | 10% | 10% | LOW ✓ |

**Generation Impact (GW vs clear-sky baseline)**:

| Region | Clear-sky Peak | Forecast Peak | Δ GW | Ramp Window |
|--------|---------------|---------------|------|-------------|
| S. Germany | 18.4 GW | 13.1 GW | -5.3 | 10:00–14:00 UTC |
| N. Italy | 12.2 GW | 10.6 GW | -1.6 | 11:00–15:00 UTC |
| C. Italy | 8.7 GW | 7.1 GW | -1.6 | 11:00–15:00 UTC |

⚠ **S. Germany ramp risk**: -5.3 GW below clear-sky during peak hours. Cloud band moving NE from France creates a 2–3h generation dip 10:00–13:00 UTC. Demand remains elevated (temp forecast 22°C).

**Trading implication**: 
- S. Germany midday hours (10–14 UTC): expect price spike as solar underperforms. Long ID position in DE for 10–14 UTC delivery.
- Italy: low risk, solar performs near baseline. No action needed.
- Watch: if cloud band accelerates (check 18z update), the dip could be shorter and sharper — higher ID price spike potential.`,
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 8 + 9000)
  }, {
    id: 'seed-4-m3',
    role: 'user',
    content: 'How does this interact with the wind picture for Germany tomorrow?',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 7)
  }, {
    id: 'seed-4-m4',
    role: 'assistant',
    content: `**Combined Wind + Solar Picture — Germany D+1**

| Hour (UTC) | Wind Gen (GW) | Solar Gen (GW) | Total RES (GW) | vs Demand | Price Signal |
|------------|--------------|----------------|----------------|-----------|--------------|
| 06:00 | 34.2 | 0.8 | 35.0 | +2.1 surplus | ↓ Bearish |
| 08:00 | 35.8 | 4.2 | 40.0 | +6.1 surplus | ↓ Bearish |
| 10:00 | 37.1 | 8.4 | 45.5 | +9.2 surplus | ↓ Strong bear |
| 12:00 | 38.2 | 10.1 | 48.3 | +10.4 surplus | ↓ Peak surplus |
| 14:00 | 37.6 | 11.8 | 49.4 | +9.8 surplus | ↓ Strong bear |
| 16:00 | 36.4 | 9.2 | 45.6 | +7.3 surplus | ↓ Bearish |
| 18:00 | 35.1 | 3.1 | 38.2 | +2.8 surplus | → Neutral |
| 20:00 | 33.8 | 0.2 | 34.0 | -1.2 deficit | ↑ Slight bull |

**Key insight**: The cloud band reducing solar 10–14 UTC is **overwhelmed by the wind upgrade**. Even with -5.3 GW solar reduction, wind adds +6.1 GW vs baseline → net RES still surges to record levels midday.

**Revised trading implication**: The earlier solar ramp risk trade (long ID 10–14 UTC) is **invalidated** by the wind picture. Wind dominates. Midday remains bearish. 

**Revised position**: Short DA for all daylight hours. The only bullish window is post-18:00 as wind ramps down and solar drops — consider long ID for 20:00–22:00 delivery.`,
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 7 + 8000)
  }]
}, {
  id: 'seed-5',
  title: 'Market Analysis Q3 Outlook',
  pinned: false,
  updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24),
  messages: [{
    id: 'seed-5-m1',
    role: 'user',
    content: 'Analyze the market trends for Q3 based on current wind and generation data.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24)
  }, {
    id: 'seed-5-m2',
    role: 'assistant',
    content: `**Q3 Market Outlook — Wind & Generation Trends**
Confidence: MED · Horizon: WK+1 to WK+12

**Generation Mix Forecast (Germany, GW average)**:

| Source | Q2 Actual | Q3 Forecast | Δ | Trend |
|--------|-----------|-------------|---|-------|
| Wind Onshore | 18.4 | 21.2 | +2.8 | ↑ Seasonal ramp |
| Wind Offshore | 9.1 | 10.4 | +1.3 | ↑ New capacity |
| Solar | 22.1 | 19.8 | -2.3 | ↓ Seasonal decline |
| Nuclear (FR) | 31.2 | 28.4 | -2.8 | ↓ Maintenance season |
| Hydro (NO/AT) | 14.3 | 16.1 | +1.8 | ↑ Snowmelt |

**Price Outlook (€/MWh)**:

| Market | Q2 Avg | Q3 Forecast | Δ | Driver |
|--------|--------|-------------|---|--------|
| DE DA | 82.4 | 74.1 | -8.3 | ↓ Wind + hydro surplus |
| FR DA | 88.7 | 91.2 | +2.5 | ↑ Nuclear maintenance |
| NO2 | 41.2 | 38.6 | -2.6 | ↓ Hydro surplus |
| GB | 94.3 | 89.7 | -4.6 | ↓ Wind ramp |

**Q3 Trading Themes**:
1. **DE short bias** — Wind capacity additions + hydro imports = structural surplus. Target: 70–75 €/MWh range.
2. **FR long bias** — Nuclear maintenance schedule peaks Aug–Sep. Tight supply = price premium.
3. **NO2-DE spread** — Hydro surplus in Norway vs wind surplus in Germany compresses the spread. Cross-border flows increase.
4. **Solar cliff** — August solar output drops 15% vs June peak. Watch for midday price floor rising.

**Key risk**: Prolonged heat wave → demand surge + solar underperformance → prices spike above forecast. Monitor temperature anomalies weekly.`,
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 + 10000)
  }]
}, {
  id: 'seed-6',
  title: 'Balancing Signals — Central Europe',
  pinned: false,
  updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 48),
  messages: [{
    id: 'seed-6-m1',
    role: 'user',
    content: 'What are the current balancing market signals for Central Europe? Show imbalance volumes and flag ramp risks.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 48)
  }, {
    id: 'seed-6-m2',
    role: 'assistant',
    content: `**Balancing Market Signals — Central Europe**
Real-time snapshot · ENTSOE data

**Imbalance Volumes (MWh, last 4 settlement periods)**:

| Period | DE Imbalance | AT Imbalance | CH Imbalance | Direction | Price (€/MWh) |
|--------|-------------|-------------|-------------|-----------|---------------|
| 15:00 | -342 | -89 | +124 | ↓ Short | 94.2 |
| 15:15 | -518 | -201 | +87 | ↓ Short | 112.7 |
| 15:30 | -287 | -156 | +203 | ↓ Short | 88.4 |
| 15:45 | +124 | +43 | +318 | ↑ Long | 71.3 |

**Ramp Risk Assessment**:

| Risk Event | Timeframe | Magnitude | Probability | Action |
|------------|-----------|-----------|-------------|--------|
| Wind ramp-up (DE) | 16:00–18:00 | +4.2 GW | HIGH | ↓ Bearish ID |
| Solar cliff (DE) | 17:30–19:00 | -8.1 GW | CERTAIN | ↑ Bullish evening |
| FR nuclear trip risk | 16:00+ | -1.4 GW | LOW | Monitor |
| AT hydro curtailment | 18:00+ | -0.8 GW | MED | Minor bull |

⚠ **Critical ramp window 17:30–19:00 UTC**: Solar cliff (-8.1 GW) coincides with wind ramp-up (+4.2 GW). Net effect: -3.9 GW generation loss. Demand rising (evening peak). Expect balancing prices to spike 90–120 €/MWh in this window.

**Net Position (Day-ahead)**:

| Country | Net Position | vs Schedule | Status |
|---------|-------------|-------------|--------|
| DE | -1,240 MW | -340 MW | ⚠ Short vs schedule |
| AT | -380 MW | -120 MW | ⚠ Short vs schedule |
| CH | +890 MW | +210 MW | ✓ Long |

**Trading implication**: DE is running short vs schedule. Evening ramp window (17:30–19:00) is high-conviction bullish. Long ID for 18:00–20:00 delivery. Target: 95–115 €/MWh entry, exit at 130+ or if wind ramp exceeds forecast.`,
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 48 + 11000)
  }]
}];
export function ChatProvider({
  children
}: {
  children: ReactNode;
}) {
  const {
    streamChat
  } = useAthena();
  const {
    profile
  } = useUserProfile();
  const [chats, setChats] = useState<Chat[]>([]);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  // Load from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('jua-chat-history');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Restore Date objects
        const hydrated = parsed.map((c: any) => ({
          ...c,
          updatedAt: new Date(c.updatedAt),
          messages: c.messages.map((m: any) => ({
            ...m,
            timestamp: new Date(m.timestamp)
          }))
        }));
        setChats(hydrated);
      } catch (e) {
        console.error('Failed to parse chat history', e);
        setChats(MOCK_CHATS);
      }
    } else {
      setChats(MOCK_CHATS);
    }
    setIsInitialized(true);
  }, []);
  // Save to localStorage
  useEffect(() => {
    if (!isInitialized) return;
    localStorage.setItem('jua-chat-history', JSON.stringify(chats));
  }, [chats, isInitialized]);
  const buildSystemPrompt = (viewContext?: ViewContext): string => {
    const viewName = viewContext?.name || 'Unknown';
    const viewDesc = viewContext?.description || '';
    const activePresets = Object.entries(profile.athenaPresets).filter(([, v]) => v).map(([k]) => {
      const labels: Record<string, string> = {
        tables: 'Always use tables for comparisons',
        deltas: 'Show run deltas (Δ) in all forecasts',
        divergence: 'Flag model divergence >15% as ⚠ signal',
        wind: 'Include wind capacity factors',
        spreads: 'Show DA vs ID price spreads',
        flows: 'Include cross-border flow analysis',
        balancing: 'Show balancing market signals',
        confidence: 'Tag confidence levels (HIGH/MED/LOW)'
      };
      return `• ${labels[k] || k}`;
    }).join('\n');
    const regionLabels: Record<string, string> = {
      'central-europe': 'Central Europe (DE, FR, NL, AT, CH)',
      nordics: 'Nordics (NO, SE, FI, DK)',
      iberia: 'Iberia (ES, PT)',
      'uk-ireland': 'UK & Ireland',
      italy: 'Italy',
      'eastern-europe': 'Eastern Europe (PL, CZ, HU, RO)',
      'all-europe': 'All Europe'
    };
    const styleInstructions: Record<string, string> = {
      concise: 'Be concise — lead with the signal, then data. No preamble.',
      detailed: 'Provide detailed analysis with full context and reasoning.',
      'tables-only': 'Respond primarily with tables. Minimal prose.'
    };
    return `You are Athena, the energy trading intelligence engine built into the Jua weather & energy platform. You serve professional energy traders, analysts, and portfolio managers.

═══ USER PROFILE ═══
Name: ${profile.firstName} ${profile.lastName}
Timezone: ${profile.timezone}
Units: ${profile.units === 'metric' ? 'Metric (°C, m/s, GW)' : 'Imperial (°F, mph)'}
Default Trading Region: ${regionLabels[profile.defaultRegion] || profile.defaultRegion}
Preferred Models: ${profile.defaultModels.join(', ')}

Address the user by first name (${profile.firstName}) occasionally to make responses feel personal. Reference their preferred region and models by default unless they specify otherwise.

═══ ATHENA RESPONSE PREFERENCES ═══
Response Style: ${profile.responseStyle.toUpperCase()} — ${styleInstructions[profile.responseStyle]}

Auto-include in every response:
${activePresets || '• (no presets active)'}

═══ WHAT THE USER IS CURRENTLY SEEING ═══
Page: "${viewName}"
${viewDesc}

This is critical context — ground your answers in what is visible on screen. Reference specific panels, values, and indicators the user can see.

═══ CORE DIRECTIVE ═══
You are NOT a general chatbot. Every response must be actionable, data-driven, and relevant to energy trading decisions. Speed and precision over verbosity.

═══ RESPONSE FORMAT ═══
1. Lead with the key insight or signal, then support with data.
2. USE MARKDOWN TABLES for ALL comparisons — model deltas, run divergences, price spreads, generation forecasts.
3. Table format example:
   | Model | Run | Wind 100m | Δ vs Prev | Signal |
   |-------|-----|-----------|-----------|--------|
   | EPT-2 | 12z | 8.4 m/s  | +0.6      | ↑ Bull |
4. Directional indicators: ↑ bullish/up · ↓ bearish/down · → stable · ⚠ divergence alert
5. Confidence tags: HIGH / MED / LOW
6. Time horizons: D+0, D+1, D+2, WK+1, WK+2
7. Units: €/MWh (prices) · GW/MW (generation) · m/s (wind) · °C (temp) · % (capacity factor)
8. Flag model divergence >15% spread as ⚠ TRADING SIGNAL
9. Always prioritize ${profile.defaultModels.join(', ')} in model comparisons.
10. Default to ${regionLabels[profile.defaultRegion] || profile.defaultRegion} unless user specifies another region.

═══ PRESET RESPONSE TEMPLATES ═══

When asked about RUN DELTAS:
| Parameter | Prev Run (06z) | Curr Run (12z) | Delta | Market Impact |
|-----------|---------------|----------------|-------|---------------|

When asked about MODEL DIVERGENCE:
| Timeframe | EPT-2 | EC AIFS | EC IFS | GFS | Max Spread | Signal |
|-----------|-------|---------|--------|-----|------------|--------|

When asked about PRICE SPREADS:
| Market | DA Price | ID Price | Spread | Direction | Driver |
|--------|----------|----------|--------|-----------|--------|

When asked about GENERATION OUTLOOK:
| Source | D+0 | D+1 | D+2 | D+3 | Trend | Confidence |
|--------|-----|-----|-----|-----|-------|------------|

When asked about CROSS-BORDER FLOWS:
| Border | Flow Dir | Capacity | Utilization | Price Δ | Opportunity |
|--------|----------|----------|-------------|---------|-------------|

═══ AVAILABLE MODELS ═══
AI Weather: EPT-2, EPT-2 Early, EPT-2 RR, EPT-2 HRRR, EPT-2e, EC AIFS, Microsoft Aurora, EC ENS
NWP: EC IFS, GFS, ICON Global, ICON-EU
Ensemble: EC Ensemble Mean, GFS Ensemble Mean

═══ ENERGY TRADING DOMAIN ═══
- Weather-to-power: wind speed → generation, cloud cover → solar CF, temp → demand
- Merit order: how weather shifts the generation stack and marginal pricing
- Cross-border: interconnector capacity, price differentials, flow direction
- Balancing: imbalance signals, frequency response, reserve activation
- Intermittency: ramp rates, forecast uncertainty, curtailment risk
- DA vs ID spreads: arbitrage from forecast updates between market closings

═══ INTERACTION RULES ═══
1. Ground answers in what the user sees on screen — reference specific widgets, charts, indicators, and values visible in their current view.
2. If ambiguous, use multiple choice:
   [A] Option one
   [B] Option two
   [C] Option three
   User presses the key to select. Keep options concise.
3. When intent is clear, answer directly — no preamble.
4. Always end with the trading implication: what does this mean for positions?
5. If user selects a UI element, focus analysis on that element.
6. Proactively flag anomalies, divergences, and opportunities you detect in the context.`;
  };
  const createChat = useCallback((initialMessage: string) => {
    const newId = Date.now().toString();
    const newMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: initialMessage,
      timestamp: new Date()
    };
    const newChat: Chat = {
      id: newId,
      title: initialMessage.slice(0, 30) + (initialMessage.length > 30 ? '...' : ''),
      pinned: false,
      updatedAt: new Date(),
      messages: [newMessage]
    };
    setChats(prev => [newChat, ...prev]);
    setActiveChatId(newId);
    return newId;
  }, []);
  const sendMessage = useCallback(async (content: string, viewContext?: ViewContext, selectedElement?: SelectedElement | null, onClearSelection?: () => void) => {
    if (!content.trim() || isStreaming) return;
    setIsStreaming(true);
    let contextMessage = '';
    if (selectedElement) {
      contextMessage = `\n\n[Context: User selected a ${selectedElement.elementType} element: "${selectedElement.description}". Content: "${selectedElement.textContent}"]`;
      onClearSelection?.();
    }
    const newMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: content,
      timestamp: new Date()
    };
    const assistantMessageId = (Date.now() + 1).toString();
    const assistantMessage: Message = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date()
    };
    let targetId = activeChatId;
    if (!targetId) {
      // Create new chat if none active
      targetId = Date.now().toString();
      const newChat: Chat = {
        id: targetId,
        title: content.slice(0, 30) + (content.length > 30 ? '...' : ''),
        pinned: false,
        updatedAt: new Date(),
        messages: [newMessage, assistantMessage]
      };
      setChats(prev => [newChat, ...prev]);
      setActiveChatId(targetId);
    } else {
      // Append to existing chat
      setChats(prev => prev.map(c => c.id === targetId ? {
        ...c,
        messages: [...c.messages, newMessage, assistantMessage],
        updatedAt: new Date()
      } : c));
    }
    // Prepare messages for API
    // We need to get the history from state, but state update might be async
    // So we construct it manually here for the API call
    const currentChat = chats.find(c => c.id === targetId);
    const history = currentChat ? currentChat.messages.slice(-20) : [];
    const apiMessages: AthenaMessage[] = [{
      role: 'system',
      content: buildSystemPrompt(viewContext)
    }, ...history.map(m => ({
      role: m.role as 'user' | 'assistant' | 'system',
      content: m.content
    })), {
      role: 'user',
      content: content + contextMessage
    }];
    await streamChat(apiMessages, chunk => {
      setChats(prev => prev.map(c => c.id === targetId ? {
        ...c,
        messages: c.messages.map(m => m.id === assistantMessageId ? {
          ...m,
          content: m.content + chunk
        } : m)
      } : c));
    }, () => setIsStreaming(false), error => {
      console.error(error);
      setChats(prev => prev.map(c => c.id === targetId ? {
        ...c,
        messages: c.messages.map(m => m.id === assistantMessageId ? {
          ...m,
          content: 'I apologize, but I encountered an error processing your request. Please try again.'
        } : m)
      } : c));
      setIsStreaming(false);
    });
  }, [activeChatId, chats, isStreaming, streamChat]);
  const handleNewChat = useCallback(() => {
    setActiveChatId(null);
  }, []);
  const togglePin = useCallback((id: string) => {
    setChats(prev => prev.map(c => c.id === id ? {
      ...c,
      pinned: !c.pinned
    } : c));
  }, []);
  const deleteChat = useCallback((id: string) => {
    setChats(prev => prev.filter(c => c.id !== id));
    setActiveChatId(prev => prev === id ? null : prev);
  }, []);
  return <ChatContext.Provider value={{
    chats,
    activeChatId,
    isStreaming,
    setActiveChatId,
    sendMessage,
    handleNewChat,
    togglePin,
    deleteChat,
    createChat
  }} data-id="element-2027">
      {children}
    </ChatContext.Provider>;
}
export function useChatHistory() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatHistory must be used within a ChatProvider');
  }
  return context;
}